import Inherit from '../../core/inherit';
import ShelfController from '../../core/shelf.controller';
import SOSAnalysisDelegate from './sos-analysis.delegate';

class SOSAnalysisController extends Inherit(ShelfController, SOSAnalysisDelegate) {
    async initialize() {
        if (this.isAuthenticated) {
            await this.fetchTagsWithSOS();

            // Check if there's a tagId in the URL query params
            const tagId = this.props.searchParams.get('tagId');
            if (tagId) {
                this.setState({ selectedTag: tagId }, () => {
                    this.handleTagChange(tagId);
                });
            }
        } else {
            this.navigateLogin();
        }
    }

    async fetchTagsWithSOS() {
        this.setState({ loading: true });
        try {
            const response = await this.data.requestTags({ hasSosTargets: true });
            const tags = response.tags || [];
            this.setState({ tags, loading: false });
        } catch (error) {
            this.props.onMessage('error', 'Error al cargar etiquetas: ' + (error.response?.data?.message || error.message));
            this.setState({ loading: false });
        }
    }

    async handleTagChange(tagId) {
        this.setState({ selectedTag: tagId, analysisLoading: true });
        try {
            // Fetch stores with this tag
            const tagItemsResponse = await this.data.requestTagItems(tagId);
            const taggedStores = (tagItemsResponse.items || []).filter((item) => item.modelType === 'Store');

            if (taggedStores.length === 0) {
                this.setState({ storesData: [], analysisLoading: false });
                return;
            }

            // Fetch analysis for each store
            const storesDataPromises = taggedStores.map(async (taggedItem) => {
                try {
                    const storeId = taggedItem.modelId;

                    // Fetch latest store check for this store (without date filters to get all)
                    const storeChecksResponse = await this.data.fetchStoreChecksByStore(storeId);
                    // Backend sends array directly, not wrapped in .data
                    const storeChecks = Array.isArray(storeChecksResponse) ? storeChecksResponse : storeChecksResponse?.data || [];

                    if (storeChecks.length === 0) {
                        return null;
                    }

                    // Get the most recent store check
                    const latestCheck = storeChecks.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

                    // Get store details
                    const storeDetails = await this.data.fetchStoreById(storeId);

                    // Use categories from the store check (already calculated)
                    const categories = latestCheck.categories || [];

                    // Calculate SOS percentage PER CATEGORY independently
                    // Formula: (faces / totalFaces) * 100 for EACH category
                    const categoriesWithSOS = categories.map((cat) => ({
                        category: cat.category,
                        faces: cat.faces || 0,
                        totalFaces: cat.totalFaces || 0,
                        // SOS = (our faces / total faces in this category) * 100
                        actualPercentage: (cat.totalFaces || 0) > 0 ? ((cat.faces || 0) / (cat.totalFaces || 0)) * 100 : 0
                    }));

                    // Calculate total faces across all categories (for display purposes)
                    const totalFaces = categories.reduce((sum, cat) => sum + (cat.faces || 0), 0);

                    const storeData = {
                        storeId: storeId,
                        storeName: storeDetails?.name || 'Nombre Desconocido',
                        lastCheckDate: latestCheck.createdAt ? new Date(latestCheck.createdAt).toLocaleDateString('es-ES') : 'N/A',
                        categories: categoriesWithSOS,
                        totalFaces: totalFaces
                    };

                    console.log(`📊 Store: ${storeData.storeName} (${storeId})`);
                    console.log(`   Total Faces (our faces across all categories): ${storeData.totalFaces}`);
                    console.log(`   Raw Categories from latestCheck:`, categories);
                    console.log(
                        `   Calculated SOS per category:`,
                        storeData.categories.map((cat) => ({
                            category: cat.category,
                            ourFaces: cat.faces,
                            totalFacesInCategory: cat.totalFaces,
                            sosPercentage: `${cat.actualPercentage.toFixed(2)}%`,
                            calculation: `${cat.faces}/${cat.totalFaces} * 100`
                        }))
                    );

                    return storeData;
                } catch (error) {
                    console.error(`Error fetching store ${taggedItem.modelId}:`, error.message);
                    return null;
                }
            });

            const storesData = (await Promise.all(storesDataPromises)).filter((store) => store !== null);

            this.setState({ storesData, analysisLoading: false });
        } catch (error) {
            this.props.onMessage('error', 'Error al cargar análisis: ' + (error.response?.data?.message || error.message));
            this.setState({ analysisLoading: false });
        }
    }

    calculateSOS(storeCheck) {
        // Calculate SOS based on faces (product facings) in the store check
        const products = storeCheck.products || [];

        // Group faces by category
        const categoryFaces = {};
        let totalFaces = 0;

        products.forEach((product) => {
            const category = product.category || 'Otros';
            const faces = product.faces || 0;

            if (!categoryFaces[category]) {
                categoryFaces[category] = 0;
            }
            categoryFaces[category] += faces;
            totalFaces += faces;
        });

        // Calculate percentages based on faces
        const categorySOS = [];
        for (const [category, faces] of Object.entries(categoryFaces)) {
            categorySOS.push({
                category,
                faces,
                actualPercentage: totalFaces > 0 ? (faces / totalFaces) * 100 : 0
            });
        }

        return {
            categories: categorySOS,
            totalFaces
        };
    }

    normalizeCategory(category) {
        // Normalize category name for comparison
        // Remove accents, convert to lowercase, replace spaces/underscores
        return (category || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remove accents
            .replace(/[_\s-]+/g, '_') // Normalize separators
            .trim();
    }

    calculateOverallCompliance(storeData) {
        const { selectedTag } = this.state;
        const tag = this.state.tags.find((t) => t._id === selectedTag);

        if (!tag || !tag.sosTargets || tag.sosTargets.length === 0) {
            return 0;
        }

        let totalCompliance = 0;
        let categoriesWithData = 0;

        console.log('🔍 Matching categories:');
        tag.sosTargets.forEach((target) => {
            const normalizedTarget = this.normalizeCategory(target.category);
            const categoryData = storeData.categories?.find((c) => this.normalizeCategory(c.category) === normalizedTarget);

            console.log(`   Tag target: "${target.category}" (normalized: "${normalizedTarget}")`);
            console.log(`   Found match: ${categoryData ? `"${categoryData.category}" with ${categoryData.actualPercentage.toFixed(2)}%` : 'NO MATCH'}`);

            if (categoryData) {
                const compliance = Math.min((categoryData.actualPercentage / target.targetPercentage) * 100, 100);
                totalCompliance += compliance;
                categoriesWithData++;
            }
        });

        return categoriesWithData > 0 ? totalCompliance / categoriesWithData : 0;
    }

    /** Tag currently selected in the filter (with its SOS targets). */
    getSelectedTagData() {
        const { tags, selectedTag } = this.state;
        return tags.find((t) => t._id === selectedTag);
    }

    /** Stores shown in the stats/table: all, or only the one picked in "Filtrar por tienda". */
    getDisplayStoresData() {
        const { storesData, selectedStore } = this.state;
        return selectedStore && selectedStore !== 'all' ? storesData.filter((store) => store.storeId === selectedStore) : storesData;
    }

    /** Category data of a store that matches a tag target (accent/separator-insensitive). */
    findCategoryData(storeData, targetCategory) {
        const normalizedTarget = this.normalizeCategory(targetCategory);
        return storeData.categories?.find((c) => this.normalizeCategory(c.category) === normalizedTarget);
    }

    /** Average compliance as a number (calculateSummaryStats returns it as a fixed-1 string). */
    getAverageCompliance(summaryStats) {
        const value = Number(summaryStats?.avgCompliance);
        return Number.isFinite(value) ? value : 0;
    }

    calculateSummaryStats(storesData) {
        if (!storesData || storesData.length === 0) {
            return {
                totalStores: 0,
                avgCompliance: 0,
                storesInTarget: 0,
                storesBelowTarget: 0
            };
        }

        let totalCompliance = 0;
        let storesInTarget = 0;
        let storesBelowTarget = 0;

        storesData.forEach((store) => {
            const compliance = this.calculateOverallCompliance(store);
            totalCompliance += compliance;

            if (compliance >= 80) {
                storesInTarget++;
            } else {
                storesBelowTarget++;
            }
        });

        return {
            totalStores: storesData.length,
            avgCompliance: (totalCompliance / storesData.length).toFixed(1),
            storesInTarget,
            storesBelowTarget
        };
    }
}

export default SOSAnalysisController;
